import type { ListBuilderConfiguration } from '../types';

/**
 * Contact Files Embedded list configuration (PD-17589)
 * Shows a person's own files on the person page
 */
export const CONTACT_FILES_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-contact-files-embedded',
			cacheTTL: 300000,
			name: 'Files',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'name-column',
					fields: [
						{
							id: 'file-name',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'fileName', objectTable: 'contact_file' },
							},
							link: {
								type: 'callback',
								enabled: true,
								callbackName: 'view',
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'theme', themeColor: 'statusInfo' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-name', text: 'Name' }],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'fileName',
							objectTable: 'contact_file',
						},
					],
				},
				{
					id: 'type-column',
					fields: [
						{
							id: 'file-type',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'fileSubtype', objectTable: 'contact_file' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-type', text: 'Type' }],
					width: 150,
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'fileSubtype',
							objectTable: 'contact_file',
						},
					],
				},
				{
					id: 'date-column',
					fields: [
						{
							id: 'upload-date',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_created_date',
									objectTable: 'contact_file',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { dateFormat: 'date-short' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h-date', text: 'Uploaded' }],
					width: 130,
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: '_created_date',
							objectTable: 'contact_file',
						},
					],
				},
				{
					id: 'uploader-column',
					fields: [
						{
							id: 'uploader-name',
							dataConfig: {
								type: 'template',
								template: '{uploadedByFirstName} {uploadedByLastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-uploader', text: 'Uploaded By' }],
					width: 140,
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'uploadedByFirstName',
							objectTable: 'contact_file',
						},
					],
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'view-action',
									label: 'View',
									iconName: 'eye',
									callbackName: 'view',
									variant: 'secondary',
									visibility: 'visible',
								},
								{
									id: 'download-action',
									label: 'Download',
									iconName: 'download',
									callbackName: 'download',
									variant: 'secondary',
									visibility: 'overflow',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									danger: true,
									visibility: 'overflow',
									requiredPermission: 'clients:contacts:update',
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'column-date-column-desc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Contact File',
			objectTable: 'contact_file',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'fk_contact__id', fieldLabel: 'Contact ID' },
				{ fieldName: 'fk_file__id', fieldLabel: 'File ID' },
				{ fieldName: 'fileName', fieldLabel: 'File Name' },
				{ fieldName: 'fileSubtype', fieldLabel: 'File Type' },
				{ fieldName: 'fileType', fieldLabel: 'Format' },
				{ fieldName: 'receivedDate', fieldLabel: 'Received' },
				{ fieldName: 'fileSize', fieldLabel: 'File Size' },
				{ fieldName: '_created_date', fieldLabel: 'Upload Date' },
				{ fieldName: 'uploadedByFirstName', fieldLabel: 'Uploader First Name' },
				{ fieldName: 'uploadedByLastName', fieldLabel: 'Uploader Last Name' },
			],
		},
	],
	tableSorts: [
		{
			id: 'date-desc',
			name: 'Upload Date (Newest First)',
			fields: [
				{
					fieldName: '_created_date',
					objectTable: 'contact_file',
					direction: 'DESC',
				},
			],
		},
		{
			id: 'date-asc',
			name: 'Upload Date (Oldest First)',
			fields: [
				{
					fieldName: '_created_date',
					objectTable: 'contact_file',
					direction: 'ASC',
				},
			],
		},
		{
			id: 'name-asc',
			name: 'File Name (A-Z)',
			fields: [
				{
					fieldName: 'fileName',
					objectTable: 'contact_file',
					direction: 'ASC',
				},
			],
		},
		{
			id: 'name-desc',
			name: 'File Name (Z-A)',
			fields: [
				{
					fieldName: 'fileName',
					objectTable: 'contact_file',
					direction: 'DESC',
				},
			],
		},
	],
};
